import { ReactElement, useEffect, useState, useCallback, useMemo } from 'react';
import { CartCounterModal } from '../CartCounterModal';
import * as S from './CartCounter.styles';
import { Button } from '@/common/ui';
import { useCartContext } from '@/entities/Cart/app/CartContext';
import { useRouter } from 'next/router';

const SEC_IN_MIN = 60;
const SHOW_MODAL = 60;
const LAST_SECOND = 1;
const DEFAULT_EXT_TIME = '5';

type TCartCounterProps = {
    isStoped?: boolean;
};

const CartCounter = ({ isStoped }: TCartCounterProps): ReactElement => {
    const [modalOpen, setModalOpen] = useState(false);
    const { push, asPath } = useRouter();
    const { expireTime, updateTimer, setExpired, cartData, config } = useCartContext();
    const [time, setTime] = useState(0);
    const isCheckoutPages = useMemo(() => asPath === '/cart/', [asPath]);

    useEffect(() => {
        const intervalId = setInterval(() => {
            setTime(() => {
                const currentTime = new Date().getTime();
                const newTime = Math.floor((expireTime - currentTime) / 1000);

                return newTime > 0 ? newTime : 0;
            });
        }, 1000);

        return () => clearInterval(intervalId);
    }, [expireTime]);

    const openCheckout = useCallback(async () => {
        await updateTimer();
        push('/cart/');
    }, [push, updateTimer]);
    const onExtend = updateTimer;

    if (time === SHOW_MODAL && !modalOpen) {
        setModalOpen(true);
    }

    if (time === LAST_SECOND && !isStoped) {
        setExpired();
        setModalOpen(false);
    }

    const minutes = `${~~(time / SEC_IN_MIN)}`.padStart(2, '0');
    const seconds = `${time % SEC_IN_MIN}`.padStart(2, '0');

    const timeLine = `${minutes}:${seconds}`;
    const showBtn = time < SHOW_MODAL;

    const percents = time / ((cartData.lifetime * SEC_IN_MIN) / 100);

    const extText = `Extend time by ${
        config?.unpaid_cart_extention_time || DEFAULT_EXT_TIME
    } minutes`;

    return (
        <S.CounterWrapper isCart={isCheckoutPages} data-test-id="Counter">
            {time > 0 && (
                <>
                    {isCheckoutPages && (
                        <>
                            <S.CounterText>
                                Your tickets are reserved and being held for
                            </S.CounterText>
                            <S.CounterLine>
                                <S.Counter>
                                    <S.CounterBlock>
                                        {minutes}
                                        <S.CounterDescription>MIN</S.CounterDescription>
                                    </S.CounterBlock>
                                    <S.CounterBlock>:</S.CounterBlock>
                                    <S.CounterBlock>
                                        {seconds}
                                        <S.CounterDescription>SEC</S.CounterDescription>
                                    </S.CounterBlock>
                                    <S.ProgressBar>
                                        <S.ProgressLine width={percents} />
                                    </S.ProgressBar>
                                </S.Counter>
                            </S.CounterLine>
                            <S.Description>
                                Your tickets will be released if checkout is not completed before
                                the timer expires.
                            </S.Description>
                            {showBtn && (
                                <S.ButtonWrapper>
                                    <Button variant="primary" onClick={onExtend}>
                                        <S.IconWhiteCross />{' '}
                                        <S.ExtendMinutes>{extText}</S.ExtendMinutes>
                                    </Button>
                                </S.ButtonWrapper>
                            )}
                        </>
                    )}
                    {modalOpen && (
                        <CartCounterModal
                            onClose={() => setModalOpen(false)}
                            onExtend={onExtend}
                            onCheckout={openCheckout}
                            duration={timeLine}
                            extText={extText}
                        />
                    )}
                </>
            )}
        </S.CounterWrapper>
    );
};

export { CartCounter };
